import { FC, useEffect, useMemo, useRef, useState } from 'react'
import * as d3 from 'd3'
import { Box, Typography, ToggleButton } from '@mui/material'
import theme from 'theme'

import useResizeObserver from 'hooks/useResizeObserver'
import { useThemeColors } from 'hooks/useThemeColors'
import { SSMYearHistory, BreakdownViewMode, SSMAssetType } from 'models'
import { getAssetTypeColor, formatCostCompact, formatCostFull } from '../../securityStackMap.utils'
import {
  BreakdownCard,
  ChartWrapper,
  ChartTooltip,
  LegendContainer,
  LegendItem,
  BreakdownToggle
} from '../ssmHistoricalOverview.styles'

const margin = { top: 24, right: 10, bottom: 40, left: 95 }

const ASSET_ORDER = [
  SSMAssetType.Devices,
  SSMAssetType.Applications,
  SSMAssetType.Networks,
  SSMAssetType.Data,
  SSMAssetType.Users
]

interface Props {
  years: SSMYearHistory[]
  assetTypes: string[]
  loading: boolean
}

const AssetTypesBreakdownChart: FC<Props> = ({ years, assetTypes, loading }) => {
  const [viewMode, setViewMode] = useState<BreakdownViewMode>('coverage')
  const svgRef = useRef<SVGSVGElement | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const dimensions = useResizeObserver(wrapperRef)
  const getColor = useThemeColors()

  const assets = assetTypes.length > 0 ? assetTypes : ASSET_ORDER.map((a) => a.toString())

  const hasData = useMemo(() => {
    return years.some((y) => {
      const breakdownData = y.asset_types_data || []
      return breakdownData.some((b) => (viewMode === 'coverage' ? b.count > 0 : b.cost > 0))
    })
  }, [years, viewMode])

  const hasAnyData = useMemo(() => {
    return years.some((y) => {
      const breakdownData = y.asset_types_data || []
      return breakdownData.some((b) => b.count > 0 || b.cost > 0)
    })
  }, [years])

  useEffect(() => {
    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    if (!wrapperRef.current || !years.length || loading || !hasData) return

    const { width: containerWidth, height: containerHeight } = dimensions || wrapperRef.current.getBoundingClientRect()
    if (!containerWidth || !containerHeight) return
    const width = containerWidth - margin.left - margin.right
    const height = containerHeight - margin.top - margin.bottom

    svg.attr('width', containerWidth).attr('height', containerHeight)

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`)

    const tooltip = d3.select(tooltipRef.current)

    /* Prepare data for d3.stack */
    const stackData = years.map((y) => {
      const entry: Record<string, any> = { year: String(y.year) }
      const breakdownData = (y.asset_types_data || []) as { name: string; count: number; cost: number }[]

      assets.forEach((asset) => {
        const item = breakdownData.find((b) => b.name === asset)
        entry[asset] = viewMode === 'coverage' ? item?.count || 0 : item?.cost || 0
      })
      return entry
    })

    /* D3 stack */
    const stackGenerator = d3.stack().keys(assets)
    const layers = stackGenerator(stackData as any)

    /* Scales */
    const isLargeScreen = window.innerWidth >= 1920
    const xScale = d3
      .scaleBand()
      .domain(years.map((y) => String(y.year)))
      .range([0, width])
      .paddingInner(isLargeScreen ? 28 / (121 + 28) : 0.15)
      .paddingOuter(isLargeScreen ? 0.2 : 0.1)

    const yMax = d3.max(layers[layers.length - 1] || [], (d) => d[1]) || 1
    const yScale = d3.scaleLinear().domain([0, yMax]).range([height, 0]).nice()

    /* Grid lines */
    const yDomain = yScale.domain()
    const yTicks = [yDomain[0], (yDomain[0] + yDomain[1]) / 2, yDomain[1]]
    g.selectAll('.grid-line')
      .data(yTicks)
      .join('line')
      .attr('class', 'grid-line')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', (d) => yScale(d))
      .attr('y2', (d) => yScale(d))
      .attr('stroke', getColor('chart-grid'))
      .attr('stroke-width', 1)
      .attr('stroke-linecap', 'round')
      .attr('stroke-dasharray', '8 8')
      .attr('opacity', 0.5)

    /* Color map */
    const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)
    const colorMap: Record<string, string> = {}
    assets.forEach((asset) => {
      colorMap[asset] = getAssetTypeColor(capitalize(asset) as SSMAssetType)
    })

    /* Render stacked bars */
    const barWidth = isLargeScreen ? 121 : xScale.bandwidth()
    const barOffset = (xScale.bandwidth() - barWidth) / 2

    const MIN_BAR_HEIGHT = 3

    g.selectAll('.layer')
      .data(layers)
      .join('g')
      .attr('class', 'layer')
      .attr('fill', (layer) => colorMap[layer.key] || '#e0e0e0')
      .selectAll('rect')
      .data((layer) => layer)
      .join('rect')
      .attr('x', (d) => (xScale(String(d.data.year)) || 0) + barOffset)
      .attr('width', barWidth)
      .attr('y', (d) => {
        const rawHeight = yScale(d[0]) - yScale(d[1])
        const segmentValue = d[1] - d[0]
        if (segmentValue > 0 && rawHeight < MIN_BAR_HEIGHT + 1) {
          return yScale(d[1]) - (MIN_BAR_HEIGHT + 1 - rawHeight)
        }
        return yScale(d[1])
      })
      .attr('height', (d) => {
        const rawHeight = yScale(d[0]) - yScale(d[1])
        const segmentValue = d[1] - d[0]
        if (segmentValue <= 0) return 0
        return Math.max(MIN_BAR_HEIGHT, rawHeight - 1)
      })
      .attr('rx', 4)

    /* Invisible hover zones covering full bar height per year for tooltip on small bars */
    const buildTooltipHtml = (d: Record<string, any>) => {
      const total = assets.reduce((sum, a) => sum + (Number(d[a]) || 0), 0)
      const formatVal = (val: number) =>
        viewMode === 'coverage'
          ? `<span class='tooltip-value'>${val}</span> <span class='tooltip-value-unit'>product${val !== 1 ? 's' : ''}</span>`
          : `<span class='tooltip-value'>$${formatCostFull(val)}</span>`

      const breakdownRows = assets
        .map((a) => {
          const val = Number(d[a]) || 0
          return (
            `<div class='tooltip-row'>` +
            `<div class='tooltip-indicator' style='background:${getAssetTypeColor(capitalize(a) as SSMAssetType)}'></div>` +
            `<span class='tooltip-label'>${capitalize(a)}:</span>` +
            `<span style='margin-left:auto'>${formatVal(val)}</span>` +
            `</div>`
          )
        })
        .join('')

      return (
        `<div class='tooltip-title-row'>` +
        `<span>${d.year}</span>` +
        `<span>${formatVal(total)}</span>` +
        `</div>${breakdownRows}`
      )
    }

    g.selectAll('.hover-zone')
      .data(stackData)
      .join('rect')
      .attr('class', 'hover-zone')
      .attr('x', (d) => (xScale(String(d.year)) || 0) + barOffset)
      .attr('width', barWidth)
      .attr('y', 0)
      .attr('height', height)
      .attr('fill', 'transparent')
      .attr('cursor', 'pointer')
      .on('mouseover', function (event, d) {
        const hoveredYear = String(d.year)
        g.selectAll('.layer rect').each(function () {
          const rect = d3.select(this)
          const rectYear = rect.datum() as any
          if (String(rectYear.data.year) !== hoveredYear) {
            rect.attr('opacity', 0.5)
          }
        })
        tooltip.style('display', 'block')
      })
      .on('mouseout', function () {
        g.selectAll('.layer rect').attr('opacity', 1)
        tooltip.style('display', 'none')
      })
      .on('mousemove', function (event, d) {
        const [mouseX, mouseY] = d3.pointer(event, wrapperRef.current)

        tooltip.html(buildTooltipHtml(d)).style('display', 'block')

        const ttNode = tooltipRef.current
        let left = mouseX + 12
        let top = mouseY - 40
        if (ttNode) {
          const ttWidth = ttNode.offsetWidth
          const ttHeight = ttNode.offsetHeight
          if (left + ttWidth > containerWidth) {
            left = mouseX - ttWidth - 12
          }
          if (top + ttHeight > containerHeight) {
            top = containerHeight - ttHeight
          }
          if (top < 0) top = 0
        }
        tooltip.style('left', `${left}px`).style('top', `${top}px`)
      })

    /* X-axis */
    const xAxis = d3.axisBottom(xScale).tickSizeInner(0).tickSizeOuter(0).tickPadding(12)

    const xAxisGroup = g.append('g').attr('class', 'x-axis').attr('transform', `translate(0,${height})`).call(xAxis)

    xAxisGroup
      .selectAll('text')
      .attr('fill', getColor('calendar-day'))
      .attr('color', getColor('calendar-day'))
      .attr('font-size', '12px')
      .attr('font-weight', 500)
      .attr('font-family', theme.typography.fontFamily.primary)
      .attr('letter-spacing', '0.5px')

    xAxisGroup.select('.domain').remove()

    /* Y-axis labels */
    const yAxisLabels = g.append('g').attr('class', 'y-axis-labels')

    yTicks.forEach((tick) => {
      const textEl = yAxisLabels
        .append('text')
        .attr('x', -16)
        .attr('y', yScale(tick))
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .attr('fill', getColor('calendar-day'))
        .attr('font-family', theme.typography.fontFamily.primary)
        .attr('letter-spacing', '0.1px')

      if (viewMode === 'coverage') {
        textEl
          .append('tspan')
          .attr('font-size', '14px')
          .attr('font-weight', 500)
          .text(`${tick} `)
        textEl
          .append('tspan')
          .attr('font-size', '12px')
          .attr('font-weight', 500)
          .text(`product${tick !== 1 ? 's' : ''}`)
      } else {
        textEl
          .attr('font-size', '14px')
          .attr('font-weight', 500)
          .text(`$${formatCostCompact(tick)}`)
      }
    })
  }, [years, assets, viewMode, dimensions, loading, hasData, getColor])

  const noData = !hasAnyData && !loading
  const activeIconColor = noData ? getColor('legacy-573f56-3') : getColor('cta')
  const inactiveIconColor = getColor('text-secondary')

  const handleViewChange = (_: React.MouseEvent<HTMLElement>, newValue: BreakdownViewMode | null) => {
    if (newValue !== null) {
      setViewMode(newValue)
    }
  }

  return (
    <BreakdownCard>
      <Box className="chart-card-header">
        <Typography className="chart-card-title">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path
              d="M1.6875 13.4998L6.75 8.43732L9.97982 11.6671C10.8826 9.89091 12.3781 8.40155 14.3402 7.52798L16.3957 6.61282M16.3957 6.61282L11.9397 4.90234M16.3957 6.61282L14.6852 11.0688"
              stroke={getColor('menu-item-text')}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Asset Types Breakdown
        </Typography>
        <BreakdownToggle size="small" value={viewMode} exclusive onChange={handleViewChange} className={!hasAnyData && !loading ? 'no-data' : ''}>
          <ToggleButton value="coverage">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M3.125 4.99984C5.78802 4.99984 8.20792 3.9589 10 2.26172C11.7921 3.9589 14.212 4.99984 16.875 4.99984C16.9173 4.99984 16.9596 4.99958 17.0018 4.99906C17.3251 5.98248 17.5 7.03325 17.5 8.12488C17.5 12.7845 14.3131 16.6997 10 17.8098C5.68693 16.6997 2.5 12.7845 2.5 8.12488C2.5 7.03325 2.67491 5.98248 2.99825 4.99906C3.04043 4.99958 3.08269 4.99984 3.125 4.99984Z"
                fill={viewMode === 'coverage' ? activeIconColor : 'none'}
                stroke={viewMode === 'coverage' ? activeIconColor : inactiveIconColor}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </ToggleButton>
          <ToggleButton value="expense">
            {viewMode === 'expense' ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M10 6.25C8.96447 6.25 8.125 7.08947 8.125 8.125C8.125 9.16053 8.96447 10 10 10C11.0355 10 11.875 9.16053 11.875 8.125C11.875 7.08947 11.0355 6.25 10 6.25Z"
                  fill={activeIconColor}
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M1.25 4.0625C1.25 3.19956 1.94956 2.5 2.8125 2.5H17.1875C18.0504 2.5 18.75 3.19956 18.75 4.0625V12.1875C18.75 13.0504 18.0504 13.75 17.1875 13.75H2.8125C1.94955 13.75 1.25 13.0504 1.25 12.1875V4.0625ZM6.875 8.125C6.875 6.39911 8.27411 5 10 5C11.7259 5 13.125 6.39911 13.125 8.125C13.125 9.85089 11.7259 11.25 10 11.25C8.27411 11.25 6.875 9.85089 6.875 8.125ZM15.625 7.5C15.2798 7.5 15 7.77982 15 8.125V8.13125C15 8.47643 15.2798 8.75625 15.625 8.75625H15.6313C15.9764 8.75625 16.2563 8.47643 16.2563 8.13125V8.125C16.2563 7.77982 15.9764 7.5 15.6313 7.5H15.625ZM3.75 8.125C3.75 7.77982 4.02982 7.5 4.375 7.5H4.38125C4.72643 7.5 5.00625 7.77982 5.00625 8.125V8.13125C5.00625 8.47643 4.72643 8.75625 4.38125 8.75625H4.375C4.02982 8.75625 3.75 8.47643 3.75 8.13125V8.125Z"
                  fill={activeIconColor}
                />
                <path
                  d="M1.875 15C1.52982 15 1.25 15.2798 1.25 15.625C1.25 15.9702 1.52982 16.25 1.875 16.25C6.37504 16.25 10.7335 16.8518 14.8748 17.9791C15.867 18.2491 16.875 17.514 16.875 16.4626V15.625C16.875 15.2798 16.5952 15 16.25 15H1.875Z"
                  fill={activeIconColor}
                />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M1.875 15.625C6.431 15.625 10.8447 16.2344 15.039 17.376C15.6448 17.5409 16.25 17.0905 16.25 16.4626V15.625M3.125 3.75V4.375C3.125 4.72018 2.84518 5 2.5 5H1.875M1.875 5V4.6875C1.875 4.16973 2.29473 3.75 2.8125 3.75H16.875M1.875 5V12.5M16.875 3.75V4.375C16.875 4.72018 17.1548 5 17.5 5H18.125M16.875 3.75H17.1875C17.7053 3.75 18.125 4.16973 18.125 4.6875V12.8125C18.125 13.3303 17.7053 13.75 17.1875 13.75H16.875M18.125 12.5H17.5C17.1548 12.5 16.875 12.7798 16.875 13.125V13.75M16.875 13.75H3.125M3.125 13.75H2.8125C2.29473 13.75 1.875 13.3303 1.875 12.8125V12.5M3.125 13.75V13.125C3.125 12.7798 2.84518 12.5 2.5 12.5H1.875M12.5 8.75C12.5 10.1307 11.3807 11.25 10 11.25C8.61929 11.25 7.5 10.1307 7.5 8.75C7.5 7.36929 8.61929 6.25 10 6.25C11.3807 6.25 12.5 7.36929 12.5 8.75ZM15 8.75H15.0062V8.75625H15V8.75ZM5 8.75H5.00625V8.75625H5V8.75Z"
                  stroke={inactiveIconColor}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </ToggleButton>
        </BreakdownToggle>
      </Box>
      <ChartWrapper ref={wrapperRef} sx={{ flex: 1, minHeight: 0 }}>
        {loading ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1
            }}
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ animation: 'spin 1.8s linear infinite' }}
            >
              <path d="M20 6.25V10M29.7227 10.2773L27.0711 12.9289M33.75 20H30M12.9289 27.0711L10.2773 29.7227M10 20H6.25M12.9289 12.9289L10.2773 10.2773M20.0002 33.75V30M27.0712 27.0711L29.7229 29.7227" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M27.0713 27.0703L29.7229 29.722" stroke="#D9CED6" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M33.75 20H30" stroke="#B1A6AE" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M29.7227 10.2734L27.071 12.9251" stroke="#7F747C" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Box>
        ) : !hasData ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
              paddingBottom: '40px',
              zIndex: 1
            }}
          >
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
              <path
                d="M3 24.0002L12 15.0002L17.7419 20.7421C19.3468 17.5844 22.0056 14.9366 25.4937 13.3836L29.1479 11.7567M29.1479 11.7567L21.2262 8.71582M29.1479 11.7567L26.107 19.6784"
                stroke={getColor('dropdown-label')}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', width: 283 }}>
              <Typography
                sx={{
                  color: getColor('text-primary'),
                  fontSize: '16px',
                  fontWeight: 500,
                  fontFamily: theme.typography.fontFamily.primary,
                  letterSpacing: '0.016px',
                  lineHeight: '20px'
                }}
              >
                No Performance Data Is Available Yet
              </Typography>
              <Typography
                sx={{
                  color: getColor('text-secondary'),
                  fontSize: '14px',
                  fontFamily: theme.typography.fontFamily.primary,
                  lineHeight: '20px',
                  textAlign: 'center'
                }}
              >
                Activate CPI on the platform to start tracking your performance and see your metrics here
              </Typography>
            </Box>
          </Box>
        ) : null}
        <ChartTooltip ref={tooltipRef} />
        <svg ref={svgRef} />
      </ChartWrapper>
      {hasData && !loading ? (
        <LegendContainer>
          {ASSET_ORDER.map((asset) => (
            <LegendItem key={asset}>
              <Box className="legend-indicator" sx={{ background: getAssetTypeColor(asset) }} />
              <Typography className="legend-label">{asset}</Typography>
            </LegendItem>
          ))}
        </LegendContainer>
      ) : null}
    </BreakdownCard>
  )
}

export default AssetTypesBreakdownChart
